import {
    Component,
    forwardRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { filter, pairwise, startWith, tap } from 'rxjs/operators';

import {
    InteractionScoringConfigEntity,
    InteractionScoringEntity,
} from '../../coaching-base/config';
import { NoteCategory } from '../../coaching-base/general';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { TranslationKeyMap } from '../coaching-observation.i18n';
import { CmpsForm, CmpsFormBuilder } from '../experimental.form';
import {
    getCurrentScoringConfig,
    getScoringConfig,
} from '../observation-tag-group/observation.focus.summary.component/observation.focus.summary.model';
import {
    READONLY_OBSERVATION,
    ReadonlyObservation,
} from '../observation/observation.model';

import { ScoreFormValue } from './score-form-value.model';
import { ScoreFormFields } from './score.observation.form';

@Component({
    selector: '[cmpsObservationScore]',
    templateUrl: './score.observation.component.html',
    host: {
        class: 'cmps-observation-score',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ScoreObservationComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ScoreObservationComponent),
            multi: true,
        },
    ],
})
@TakeUntilDestroy()
export class ScoreObservationComponent
    implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
    /** @private */ _i18n = TranslationKeyMap;
    @Input() config: InteractionScoringEntity;
    @Input() defaultNoteCategory: NoteCategory;
    @Input() published: boolean;
    /** @private */ _form: CmpsForm<ScoreFormFields>;
    private _blackout: boolean;
    private _initialValue: ScoreFormValue;
    private _onChange: (_: ScoreFormValue) => void;
    private _onTouched: () => void;
    private _onValidatorChange: () => void;

    /** @private */ get _value(): ScoreFormValue {
        return this._form.form.value;
    }

    /** @private */ _showWillingness: boolean = null;
    /** @private */ _showProficiency: boolean = null;
    /** @private */ _clickedItem = null;

    constructor(
        private readonly _formBuilder: CmpsFormBuilder,
        @Optional()
        @Inject(READONLY_OBSERVATION) /** @private */
        readonly _readonly: ReadonlyObservation,
    ) {}

    buttons = [];
    buttonsLabels = []; // should be able to use translation

    lowCount = 0;
    midCount = 0;
    highCount = 0;

    ngOnInit(): void {
        this._form = this._formBuilder.create<ScoreFormFields>(ScoreFormFields);

        this._form.form.valueChanges
            .pipe(
                filter(() => !this._blackout),
                startWith(null as ScoreFormValue),
                pairwise(),
                tap<[ScoreFormValue, ScoreFormValue]>(([prev, value]) => {
                    /* 'cause of the side effects of things that touch the form, change tracking is done like this */
                    prev = prev || this._initialValue;
                    const next: ScoreFormValue = {
                        ...value,
                        trackingChanged:
                            prev.trackingChanged ||
                            value.trackingChanged ||
                            prev.focusDelivered !== value.focusDelivered ||
                            prev.opportunityToDemonstrate !==
                                value.opportunityToDemonstrate ||
                            prev.willingnessToTry !== value.willingnessToTry ||
                            prev.proficiencyDelivered !==
                                value.proficiencyDelivered ||
                            prev.consistencyDelivered !==
                                value.consistencyDelivered ||
                            prev.confidenceDelivered !==
                                value.confidenceDelivered,
                    };

                    this._form.form.patchValue(
                        {
                            trackingChanged: next.trackingChanged,
                        },
                        { emitEvent: false },
                    );

                    CMPS_APP_DEF.ENV !== 'prod' &&
                        console.log('_onChange score', next);
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this._onChange && this._onChange(next);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _loadConfig(val: ScoreFormValue): void {
        // currently used Score Config
        const configId = val ? val.scoringConfigurationId : null;
        let scoringConfig = getScoringConfig(this.config, configId);

        if (!scoringConfig && !this.published) {
            scoringConfig = getCurrentScoringConfig(this.config);

            // TODO move into createScore()
            if (scoringConfig && val) {
                val.scoringConfigurationId =
                    scoringConfig.scoringConfigurationId;
            }
        }

        this._setEvalCounts(scoringConfig, val);
        this._generateButtonLabels(scoringConfig);
    }

    private _setEvalCounts(
        scoringConfig: InteractionScoringConfigEntity,
        val: ScoreFormValue,
    ): void {
        this.buttons = [];

        if (!scoringConfig || !scoringConfig.evalRanges) {
            // either no props in scoring config, no scoring config was found, or configId is null
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (val && val.proficiencyDelivered != null) {
                this.buttons.push({
                    id: null,
                    css: 'eval-button-low-selected',
                    rangeName: null,
                    label: `${val.proficiencyDelivered}`,
                });
            }
            return;
        }

        scoringConfig.evalRanges.forEach((x: any, idx: number) => {
            let c = 0;
            for (let j = x.rangeStart; j <= x.rangeEnd; j++) {
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                this.buttons.push({
                    id: j,
                    css: x.cssType,
                    rangeName: x.rangeName,
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    label: `${j}`,
                });
                c++;
            }

            if (idx === 0) {
                this.lowCount = c;
            } else if (idx === 1) {
                this.midCount = c;
            } else if (idx === 2) {
                this.highCount = c;
            }
        });
    }

    private _generateButtonLabels(props: InteractionScoringConfigEntity): void {
        const btnslength = this.buttons.length;

        this.buttonsLabels = [];

        if (!props || !props.evalRanges) {
            // either no props in scoring config, no scoring config was found, or configId is null
            this.buttonsLabels.push({ name: null, width: '100%' });
            return;
        }

        this.buttonsLabels.push(
            ...props.evalRanges.map((x, idx) => {
                const f = {
                    name: x.rangeName,
                    width: '10%',
                };

                if (idx === 0) {
                    f.width = `${this.lowCount * (100 / btnslength)}%`;
                } else if (idx === 1) {
                    f.width = `${this.midCount * (100 / btnslength)}%`;
                } else if (idx === 2) {
                    f.width = `${this.highCount * (100 / btnslength)}%`;
                }

                return f;
            }),
        );
    }

    ngOnDestroy(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._form && this._form.destroy();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled
            ? this._form.form.disable({ emitEvent: false })
            : this._form.form.enable({ emitEvent: false });
    }

    writeValue(obj: ScoreFormValue): void {
        this._loadConfig(obj);

        this._patchForm(obj);

        this._showWillingness = obj.opportunityToDemonstrate
            ? obj.opportunityToDemonstrate
            : null;
        this._showProficiency = obj.willingnessToTry
            ? obj.willingnessToTry
            : null;
        this._clickedItem = obj.proficiencyDelivered
            ? obj.proficiencyDelivered
            : null;
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this._form.form.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false,
        });
        this._form.checkWarningsErrors();
        return this._form.form.errors;
    }

    private _patchForm(value: ScoreFormValue): void {
        this._blackout = true;

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        this._initialValue = value || ({} as ScoreFormValue);
        this._form.clearWarningsErrors();
        this._form.form.reset(this._initialValue, { emitEvent: false });

        this._blackout = false;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectedDemonstrate(value) {
        if (!this.published) {
            const bValue = JSON.parse(value);
            if (!bValue) {
                this._showProficiency = bValue;
                this._clickedItem = null;
            }
            this._showWillingness = bValue;

            this._form.form.patchValue({
                [ScoreFormFields.WILLINGNESS_TRY.name]: null,
            });
            this._form.form.patchValue({
                [ScoreFormFields.PROFICIENCY_DELIVERED.name]: null,
            });
            this._form.form.patchValue({
                [ScoreFormFields.OPP_TO_DEMONSTRATE.name]:
                    this._showWillingness,
            });
            this._onChange(this._form.form.value);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectedWillingness(value) {
        if (!this.published) {
            const bValue = JSON.parse(value);
            if (!bValue) {
                this._clickedItem = null;
                this._form.form.patchValue({
                    [ScoreFormFields.PROFICIENCY_DELIVERED.name]: null,
                });
            }
            this._showProficiency = bValue;

            this._form.form.patchValue({
                [ScoreFormFields.WILLINGNESS_TRY.name]: this._showProficiency,
            });
            this._onChange(this._form.form.value);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onProficiencySelected(value) {
        if (!this.published) {
            this._clickedItem = value;

            this._form.form.patchValue({
                [ScoreFormFields.PROFICIENCY_DELIVERED.name]: this._clickedItem,
            });
            this._onChange(this._form.form.value);
        }
    }
}
